import { useCallback } from "react";
import { container } from "tsyringe";
import { XMarkIcon } from "@heroicons/react/20/solid";
import ProjectTeamRoleContributorsResponseResource from "common/resources/Project/Config/Team/Role/ProjectTeamRoleContributorsResponseResource";
import Button, { EButtonColor, EButtonVariant } from "components/elements/Button";
import Modal from "components/elements/Modal";
import Typography from "components/elements/Typography";
import I18n from "components/materials/I18n";
import { useProjectTeamContext } from "components/pages/ProfessionalSpace/contexts/ProjectTeam";
import { useProjectId } from "hooks/useProjectId";
import ContributorService from "services/projects/ContributorService";
import RoleService from "services/projects/RoleService";
import { translateISO369 } from "utils/I18nText";
import classes from "./classes.module.scss";

type IProps = {
	role: ProjectTeamRoleContributorsResponseResource;
	isOpen: boolean;
	onClose: () => void;
	onDeleted: (role: ProjectTeamRoleContributorsResponseResource) => void;
};

const contributorService = container.resolve(ContributorService);
const roleService = container.resolve(RoleService);

export default function DeleteRoleModal(props: IProps) {
	const { projectId } = useProjectId();
	const { projectTeamId } = useProjectTeamContext();

	const deleteAllContributors = useCallback(() => {
		return Promise.all(
			props.role.contributors.map((contributor) => {
				return contributorService.delete(projectId, projectTeamId, contributor.id);
			}),
		);
	}, [projectId, projectTeamId, props.role.contributors]);

	const onDeleteRole = useCallback(() => {
		deleteAllContributors()
			.then(() => roleService.delete(projectId, projectTeamId, props.role.id))
			.then(() => props.onDeleted(props.role));
	}, [deleteAllContributors, projectId, projectTeamId, props]);

	return (
		<Modal isOpen={props.isOpen} onClose={props.onClose} className={classes["root"]}>
			<div className={classes["header"]}>
				<Typography type="p" size="large" weight="bold">
					{I18n.asset.pages.professional_space.tabs.team.delete_role_modal.title}
				</Typography>
				<XMarkIcon className={classes["icon"]} onClick={props.onClose} />
			</div>

			<div className={classes["text-container"]}>
				<Typography type="p">
					{I18n.trslt(I18n.asset.pages.professional_space.tabs.team.delete_role_modal.text_1, { role: translateISO369(props.role.roleTitle) ?? "undefined" })}
				</Typography>
				<Typography type="p">{I18n.asset.pages.professional_space.tabs.team.delete_role_modal.text_2}</Typography>
			</div>

			<div className={classes["buttons-container"]}>
				<Button variant={EButtonVariant.TEXT} color={EButtonColor.PRIMARY} onClick={props.onClose}>
					{I18n.asset.pages.professional_space.tabs.team.delete_role_modal.cancel}
				</Button>
				<Button variant={EButtonVariant.CONTAINED} color={EButtonColor.PRIMARY} onClick={onDeleteRole}>
					{I18n.asset.pages.professional_space.tabs.team.delete_role_modal.confirm}
				</Button>
			</div>
		</Modal>
	);
}
